import type { NextPage } from "next"
import React, { useMemo, useState } from "react"
import { postContact } from "services/node-api/post-contact"

import { Box } from "components/shared/box"

import { useInput } from "../components/hooks/use-input"
import { useModal } from "../components/hooks/use-modal"
import { InfoModal } from "../components/modals/info-modal"
import { Aim } from "../components/shared/aim"
import { BackgroundImage } from "../components/shared/background-img"
import { Mail } from "../components/shared/icons/mail"
import { InputText } from "../components/shared/input-text"
import { Button } from "../components/shared/landing/button"
import { GradientBox } from "../components/shared/landing/gradient-box"
import { Text } from "../components/shared/text"
import { Textarea } from "../components/shared/textarea"

type PostContactResponse = { body: string; statusCode: number }

function getMessageComponent(isError: boolean) {
  return isError ? (
    <span>
      You message has not sent. We are working on it. You can still send us{" "}
      <a href="mailto:support@cryptobots.me" className="underline underline-offset-1">
        mail
      </a>
    </span>
  ) : (
    <span>Your message has successfully sent!</span>
  )
}

const ContactsPage: NextPage = () => {
  const [isLoafing, setIsLoading] = useState<boolean>(false)

  const [name, bindName, resetName] = useInput<string>("")
  const [email, bindEmail, resetEmail] = useInput<string>("")
  const [message, bindMessage, resetMessage] = useInput<string>("")

  const [formIsError, setFormIsError] = useState<boolean>(false)
  const { modalIsOpen, openModal, closeModal } = useModal()

  const formIsInvalid = useMemo<boolean>(() => {
    return !(name && email && message)
  }, [name, email, message])

  const sendForm = async () => {
    setIsLoading(true)

    try {
      const response: PostContactResponse = await postContact({ name, email, message })

      if (response?.statusCode == 200) {
        resetName()
        resetEmail()
        resetMessage()

        setFormIsError(false)
      } else {
        console.log(JSON.parse(response.body))
        setFormIsError(true)
      }
    } catch (e) {
      console.error(e)
      setFormIsError(true)
    } finally {
      setIsLoading(false)
    }

    openModal()
  }

  return (
    <Box className="flex-1">
      {modalIsOpen && (
        <InfoModal
          type={formIsError ? "error" : "success"}
          message={getMessageComponent(formIsError)}
          close={() => closeModal()}
        />
      )}
      <BackgroundImage imageSrc="/images/pages/contact/bg-telescope.jpg" className="bg-cover bg-top bg-no-repeat">
        <Box className="min-h-[calc(100vh-250px)] my-[60px] flex flex-1 flex-col justify-center items-center">
          <div className="mt-[60px] w-full max-w-[1200px] flex-col md:flex-row text-white px-4 md:px-6 flex justify-center items-center">
            <div className="flex flex-1 shrink-0 max-w-[260px] md:max-w-full">
              <div className="flex flex-col my-auto md:border-l-2 md:border-primary md:min-h-[320px]">
                <section className="flex flex-col md:my-auto md:ml-[52px] text-center md:text-left">
                  <Text as="h2">Contact us</Text>
                  <p className="mt-4 text-white text-table font-medium">Please contact us via the form or email us</p>
                  <span className="mt-10 flex justify-center md:justify-start">
                    <Mail h={24} w={24} />
                    <a
                      className="ml-3 text-sm font-bold text-white underline underline-offset-1"
                      href="mailto:support@cryptobots.me"
                    >
                      support@cryptobots.me
                    </a>
                  </span>
                </section>
              </div>
            </div>
            <Aim className="flex-1 mt-7 w-full md:mt-0">
              <GradientBox className={"p-10 drop-shadow-md"}>
                <InputText label="Name" type="text" {...bindName} />
                <InputText className="mt-4" label="Email" type="email" {...bindEmail} />
                <Textarea className="mt-4" label="Comment or question" {...bindMessage} />

                <div className="mt-8 flex justify-center md:justify-start">
                  <Button onClick={sendForm} disabled={formIsInvalid} loading={isLoafing}>
                    Send a message
                  </Button>
                </div>
              </GradientBox>
            </Aim>
          </div>
        </Box>
      </BackgroundImage>
    </Box>
  )
}
export default ContactsPage
